import { useContext, useEffect, useRef, useState } from 'react'
import { kExternalStorageProvider, kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { getExternalAccountFiles } from '@/user_files/external-storage/fetch/get-account-files'
import { postImportFolder } from '@/user_files/external-storage/fetch/post-import-folder'
import { ExternalFileItem, ImportFolderResponse } from '@/user_files/external-storage/schemas'
import { ExternalStorageActions } from '@/user_files/external-storage/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { selectCurrentFolder, selectDriveListSortForMatter } from '@/user_files/folders/store/selectors'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterDriveSyncDismissReason, MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { ApiError } from '@/util/errors/api-error'
import type { ExternalFolderSelection } from '@/user_files/external-storage/types'

export interface ExternalFolderPickerSuccess {
  account: ConnectedAccount
  folder: ExternalFileItem
  response: ImportFolderResponse
}

interface ExternalFolderBreadcrumbEntry {
  id: string
  name: string
}

interface ExternalFolderPickerBaseOptions {
  account: ConnectedAccount
  currentExternalFolderId?: string | null
  onOpenChange: (open: boolean) => void
  open: boolean
  /** Discoverability surface that led here — see analytics/README.md ("Matter Drive Sync"). Rendered
   * from both Matter Settings and Matter Overview today; defaults to Matter Settings for callers
   * that don't pass this explicitly. */
  matterDriveSyncEntrySource?: MatterDriveSyncEntrySource
}

export type UseExternalFolderPickerOptions = ExternalFolderPickerBaseOptions &
  (
    | {
        matterId: string
        onSuccess?: (result: ExternalFolderPickerSuccess) => void
        replaceExistingMapping?: boolean
        onSelect?: never
      }
    | {
        /** Before matter creation, confirm a local choice without starting synchronization. */
        onSelect: (selection: ExternalFolderSelection) => void
        matterId?: never
        onSuccess?: never
        replaceExistingMapping?: never
      }
  )

export function useExternalFolderPicker({
  account,
  currentExternalFolderId,
  matterId,
  onOpenChange,
  onSuccess,
  onSelect,
  open,
  replaceExistingMapping = false,
  matterDriveSyncEntrySource = MatterDriveSyncEntrySource.MatterSettings,
}: UseExternalFolderPickerOptions) {
  const dispatch = useAppDispatch()
  const currentFolder = useAppSelector((state) => (matterId ? selectCurrentFolder(state) : null))
  const driveListSort = useAppSelector((state) => selectDriveListSortForMatter(state, { matterId }))
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getDriveSyncOrganizationId(userAccountData)
  const [items, setItems] = useState<ExternalFileItem[]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<ExternalFolderBreadcrumbEntry[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [browseAttempt, setBrowseAttempt] = useState(0)
  const didPreselectCurrentFolderRef = useRef(false)
  // Tracks whether MatterDriveSyncFolderMapped already fired in this open session, so closing the
  // modal afterwards (success path) does not also emit MatterDriveSyncFolderPickerDismissed.
  const mappedThisSessionRef = useRef(false)
  // Set synchronously by the Cancel button's onClick, read by handleClose (shared with
  // backdrop/Escape dismissal via Modal's onOpenChange) to distinguish the two dismiss paths.
  const dismissReasonRef = useRef<MatterDriveSyncDismissReason>(MatterDriveSyncDismissReason.Backdrop)
  // useRef guard (not just the [open] deps array) because React.StrictMode double-invokes mount
  // effects on the same instance in development; trackEvent isn't idempotent. See SettingsPage.tsx
  // for the same pattern.
  const pickerOpenedTrackedRef = useRef(false)

  const browsedFolderId = breadcrumbs.at(-1)?.id
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]
  // Microsoft Graph cannot provide a complete listing of a user's SharePoint
  // sites. At the root, users can search by name or paste a site URL.
  const showAddSharePointSite = account.provider === kExternalStorageProvider.SHAREPOINT && breadcrumbs.length === 0

  // MatterDriveSyncFolderPickerOpened — one per picker open, not per re-render while open.
  useEffect(() => {
    if (!open || !matterId || pickerOpenedTrackedRef.current) return

    pickerOpenedTrackedRef.current = true
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncFolderPickerOpened,
      { matterId, organizationId, externalStorageProvider: account.provider, replaceExistingMapping, matterDriveSyncEntrySource },
      trackEvent
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per open transition only
  }, [open])

  useEffect(() => {
    if (!open) return

    // The SharePoint root has nothing to list — search and pasted URLs are
    // handled separately. Skipping the request also avoids stranding a
    // "Failed to load folders." error in a branch that renders no folder list.
    if (showAddSharePointSite) {
      setItems([])
      setError(null)
      setLoadingFiles(false)
      return
    }

    let active = true

    setLoadingFiles(true)
    setError(null)
    setItems([])
    if (!didPreselectCurrentFolderRef.current && !browsedFolderId) {
      setSelectedFolderId(currentExternalFolderId ?? null)
      didPreselectCurrentFolderRef.current = true
    }

    getExternalAccountFiles(account.id, browsedFolderId)
      .then((response) => {
        if (!active) return
        setItems(response.items)
      })
      .catch(() => {
        if (!active) return
        setError('Failed to load folders.')
        if (matterId)
          trackMatterDriveSyncEvent(
            AnalyticsEvent.MatterDriveSyncFolderBrowseFailed,
            {
              matterId,
              organizationId,
              externalStorageProvider: account.provider,
              folderPickerBreadcrumbDepth: breadcrumbs.length,
              matterDriveSyncEntrySource,
            },
            trackEvent
          )
      })
      .finally(() => {
        if (!active) return
        setLoadingFiles(false)
      })

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- organizationId/entrySource are stable for the modal's lifetime; adding them would re-trigger browsing on every render
  }, [account.id, browsedFolderId, currentExternalFolderId, open, showAddSharePointSite, browseAttempt])

  const reset = () => {
    setItems([])
    setBreadcrumbs([])
    setSelectedFolderId(null)
    didPreselectCurrentFolderRef.current = false
    setSearch('')
    setError(null)
    setIsImporting(false)
    mappedThisSessionRef.current = false
    dismissReasonRef.current = MatterDriveSyncDismissReason.Backdrop
    pickerOpenedTrackedRef.current = false
  }

  const handleClose = () => {
    if (matterId && !mappedThisSessionRef.current) {
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncFolderPickerDismissed,
        {
          matterId,
          organizationId,
          externalStorageProvider: account.provider,
          replaceExistingMapping,
          matterDriveSyncDismissReason: dismissReasonRef.current,
          hadFolderSelected: !!selectedFolder && !selectedFolderIsUnavailable,
          folderPickerBreadcrumbDepth: breadcrumbs.length,
          matterDriveSyncEntrySource,
        },
        trackEvent
      )
    }
    reset()
    onOpenChange(false)
  }

  const handleDrillIn = (folder: ExternalFileItem) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }])
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleBreadcrumbNav = (index: number) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1))
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleRootBreadcrumbNav = () => {
    setBreadcrumbs([])
    setSelectedFolderId(null)
    setSearch('')
  }

  const handleConnect = async () => {
    if (!selectedFolder || selectedFolderIsUnavailable) return

    if (onSelect) {
      onSelect({ accountId: account.id, folderId: selectedFolder.id, path: [...breadcrumbs, selectedFolder].map((folder) => folder.name).join(' / ') })
      handleClose()
      return
    }
    if (!matterId) return

    setIsImporting(true)
    setError(null)

    try {
      const response = await postImportFolder({
        accountId: account.id,
        externalFolderId: selectedFolder.id,
        matterId,
        replaceExistingMapping,
      })
      // Reused mappings keep their Paxton root and entity identities. Drop only that root's
      // cached listing so rows from its previous activation cannot appear before the new provider
      // snapshot finishes; globally shared folder/file records remain available to by-id users.
      if (!response.created_mapping) {
        dispatch(UserFoldersActions.clearFolderChildren({ folderId: response.user_folder_id }))
        dispatch(UserFilesActions.clearFolderFileList({ folderId: response.user_folder_id }))
      }
      // The response acknowledges the mapping write but does not contain its new public state.
      // Force a post-write read so an older in-flight response cannot retain a completed sync
      // timestamp. Refresh the visible Matter Files listing too: reused links become managed as
      // soon as the mapping is active, before the new provider snapshot finishes.
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId, force: true }))
      const currentFolderIsPendingReusedRoot = !response.created_mapping && currentFolder?.id === response.user_folder_id
      if (currentFolder?.matter_id === matterId && !currentFolderIsPendingReusedRoot) {
        dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolder.id, sort: driveListSort }))
        dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolder.id, sort: driveListSort }))
      }
      mappedThisSessionRef.current = true
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncFolderMapped,
        {
          matterId,
          organizationId,
          externalStorageProvider: account.provider,
          externalSyncMappingId: response.external_sync_mapping_id,
          createdMapping: response.created_mapping,
          replaceExistingMapping,
          observedFilesCount: response.observed_files_count,
          createdFileLinksCount: response.created_file_links_count,
          observedFoldersCount: response.observed_folders_count,
          matterDriveSyncEntrySource,
        },
        trackEvent
      )
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `Syncing folders and files from ${providerLabel}. You can keep working.`,
          durationMs: kToastDurationMediumMs,
        })
      )
      handleClose()
      onSuccess?.({ account, folder: selectedFolder, response })
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping({ matterId, force: true }))
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'A folder is already connected to this matter.', durationMs: kToastDurationMediumMs }))
        reset()
        onOpenChange(false)
      } else {
        setError('Failed to connect folder. Please try again.')
        setIsImporting(false)
      }
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncFolderMapFailed,
        {
          matterId,
          organizationId,
          externalStorageProvider: account.provider,
          replaceExistingMapping,
          matterDriveSyncEntrySource,
          ...getAnalyticsApiErrorProps(error),
        },
        trackEvent
      )
    }
  }

  const folders = items.filter((item) => item.is_folder)
  const filteredFolders = folders.filter((folder) => folder.name.toLowerCase().includes(search.toLowerCase()))
  const unavailableFolderIds = new Set(
    folders.filter((folder) => folder.external_sync_matter_id != null && folder.external_sync_matter_id !== matterId).map((folder) => folder.id)
  )
  const selectedFolder = selectedFolderId ? filteredFolders.find((item) => item.id === selectedFolderId) ?? null : null
  const selectedFolderIsCurrent = replaceExistingMapping && selectedFolder?.id === currentExternalFolderId
  const selectedFolderIsUnavailable = selectedFolder ? unavailableFolderIds.has(selectedFolder.id) : false
  const canConfirm = Boolean(selectedFolder) && !selectedFolderIsCurrent && !selectedFolderIsUnavailable && !isImporting && !loadingFiles

  return {
    breadcrumbs,
    filteredFolders,
    selectedFolderId,
    unavailableFolderIds,
    search,
    loadingFiles,
    isImporting,
    error,
    showAddSharePointSite,
    canConfirm,
    canRetryBrowse: error === 'Failed to load folders.',
    handleClose,
    handleDrillIn,
    handleBreadcrumbNav,
    handleRootBreadcrumbNav,
    handleConnect,
    setSearch,
    setSelectedFolderId,
    retryBrowse: () => setBrowseAttempt((attempt) => attempt + 1),
    markCancelled: () => {
      dismissReasonRef.current = MatterDriveSyncDismissReason.Cancel
    },
  }
}
